import { getHubUrl } from "@internal/auth";
import { useEffect, useState } from "react";
import { MobileShell } from "@/components/layout/MobileShell";
import { ParentLayout } from "@/components/layout/ParentLayout";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { useObservable } from "@/hooks/useObservable";
import { useProfile } from "@/hooks/useProfile";
import { installPrompt } from "@/lib/installPrompt";
import {
  currentWeekBounds,
  formatWeekRange,
  transitionWeekAfter,
} from "@/lib/week";
import { familyService } from "@/services/family.service";
import { notificationsService } from "@/services/notifications.service";
import {
  type PermissionStatus,
  pushSubscriptionsService,
} from "@/services/pushSubscriptions.service";
import type {
  CloseWeekPreference,
  FlagPreference,
  MaxBackdateDays,
  NotificationPreferencesData,
  TimedPreference,
  WeekStartDay,
} from "@/types/domain";

type DaysAfterClose = -3 | -2 | -1 | 0 | 1 | 2 | 3;

const DAYS_AFTER_CLOSE_VALUES: DaysAfterClose[] = [-3, -2, -1, 0, 1, 2, 3];

function daysAfterCloseLabel(value: DaysAfterClose): string {
  if (value === 0) return "På avslutningsdagen";
  if (value === -1) return "Dagen før";
  if (value === 1) return "Dagen etter";
  if (value < 0) return `${Math.abs(value)} dager før`;
  return `${value} dager etter`;
}

export function SettingsScreen() {
  const { profile } = useProfile();
  if (!profile) return null;
  const isParent = profile.data.role === "parent";

  const content = (
    <div
      style={{
        maxWidth: 640,
        margin: "0 auto",
        padding: isParent ? 0 : "52px 20px 28px",
        display: "flex",
        flexDirection: "column",
        gap: 24,
      }}
      data-testid="settings-screen"
    >
      <Header />
      <InstallSection />
      {isParent && <WeekSection />}
      <NotificationsSection />
      <AccountSection />
    </div>
  );

  return isParent ? (
    <ParentLayout>{content}</ParentLayout>
  ) : (
    <MobileShell>{content}</MobileShell>
  );
}

function Header() {
  return (
    <div>
      <div
        style={{
          fontSize: 12,
          color: "var(--ink-3)",
          textTransform: "uppercase",
          letterSpacing: 0.7,
          fontWeight: 700,
        }}
      >
        Innstillinger
      </div>
      <div
        style={{
          fontFamily: "var(--font-display)",
          fontStyle: "italic",
          fontSize: 30,
          fontWeight: 400,
          marginTop: 2,
          letterSpacing: -0.6,
        }}
      >
        Appen og varsler
      </div>
    </div>
  );
}

function SectionTitle({ children }: { children: string }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: 0.8,
        color: "var(--ink-2)",
        marginBottom: 10,
      }}
    >
      {children}
    </div>
  );
}

function InstallSection() {
  const state = useObservable(installPrompt.state$, installPrompt.snapshot);
  if (state.installed) return null;
  return (
    <div data-testid="install-section">
      <SectionTitle>Installer app</SectionTitle>
      <Card>
        <div
          style={{
            fontSize: 14,
            lineHeight: 1.5,
            color: "var(--ink-2)",
            marginBottom: 14,
          }}
        >
          Legg Ukepenger til på hjem-skjermen for å åpne den raskt — og for å
          motta varsler på iPhone og iPad.
        </div>
        <InstallActions platform={state.platform} canPrompt={state.canPrompt} />
      </Card>
    </div>
  );
}

function InstallActions({
  platform,
  canPrompt,
}: {
  platform: "chromium" | "ios-safari" | "generic";
  canPrompt: boolean;
}) {
  if (canPrompt && platform === "chromium") {
    return (
      <Button
        data-testid="install-button"
        onClick={() => {
          void installPrompt.prompt();
        }}
      >
        Installer app
      </Button>
    );
  }
  if (platform === "ios-safari") {
    return (
      <div
        data-testid="install-ios-instructions"
        style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.6 }}
      >
        Trykk på <IosShareGlyph /> i Safari-menyen, og velg{" "}
        <strong>«Legg til på Hjem-skjerm»</strong>.
      </div>
    );
  }
  return (
    <div
      data-testid="install-generic-instructions"
      style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.6 }}
    >
      Åpne appen i Chrome eller Edge for å installere den, eller bruk{" "}
      <strong>«Legg til på Hjem-skjerm»</strong> i nettlesermenyen.
    </div>
  );
}

function IosShareGlyph() {
  return (
    <span
      style={{
        display: "inline-flex",
        verticalAlign: "-3px",
        margin: "0 2px",
      }}
    >
      <svg width="16" height="20" viewBox="0 0 16 20" fill="none">
        <title>Del-knappen i Safari</title>
        <path
          d="M8 2v11"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M4 6l4-4 4 4"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
        <path
          d="M2 9v8a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V9"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          fill="none"
        />
      </svg>
    </span>
  );
}

function AccountSection() {
  return (
    <div data-testid="account-section">
      <SectionTitle>Konto</SectionTitle>
      <Card>
        <a
          href={getHubUrl("/innstillinger")}
          target="_blank"
          rel="noopener"
          data-testid="min-konto-link"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            fontSize: 14,
            color: "var(--forest)",
            textDecoration: "none",
            fontWeight: 600,
          }}
        >
          Min konto
          <span aria-hidden style={{ fontSize: 12 }}>
            ↗
          </span>
        </a>
        <div
          style={{
            marginTop: 8,
            fontSize: 13,
            color: "var(--ink-3)",
            lineHeight: 1.5,
          }}
        >
          Endre passord, e-post og familiemedlemmer.
        </div>
      </Card>
    </div>
  );
}

const MAX_BACKDATE_DAYS_VALUES: MaxBackdateDays[] = [0, 1, 2, 3, 7];

function maxBackdateDaysLabel(value: MaxBackdateDays): string {
  if (value === 0) return "Bare i dag";
  if (value === 1) return "I dag og i går";
  if (value === 7) return "Hele uka tilbake";
  return `${value} dager tilbake`;
}

function WeekSection() {
  const family = useObservable(familyService.state$, familyService.snapshot);
  const [pendingChoice, setPendingChoice] = useState<WeekStartDay | null>(null);
  const [saving, setSaving] = useState(false);
  const current = family.settings?.week_start_day ?? 1;
  const maxBackdateDays = (family.settings?.max_backdate_days ??
    1) as MaxBackdateDays;

  if (!family.settings) return null;

  return (
    <div data-testid="week-section">
      <SectionTitle>Uka</SectionTitle>
      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <Card>
          <div style={{ fontWeight: 600, fontSize: 14 }}>Ukestart</div>
          <div
            style={{
              fontSize: 12,
              color: "var(--ink-3)",
              marginTop: 2,
              marginBottom: 12,
            }}
          >
            Hvilken dag uka starter på.
          </div>
          <div
            style={{
              display: "inline-flex",
              border: "1px solid var(--line-2)",
              borderRadius: 10,
              overflow: "hidden",
            }}
          >
            <SegmentButton
              label="Man – søn"
              active={current === 1}
              onClick={() => current !== 1 && setPendingChoice(1)}
              testId="week-start-day-monday"
            />
            <SegmentButton
              label="Søn – lør"
              active={current === 0}
              onClick={() => current !== 0 && setPendingChoice(0)}
              testId="week-start-day-sunday"
            />
          </div>
        </Card>
        <Card>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            Tilbake i tid for barn
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--ink-3)",
              marginTop: 2,
              marginBottom: 12,
            }}
          >
            Hvor langt tilbake barna kan logge jobber. Stengte uker er alltid
            låst.
          </div>
          <select
            data-testid="max-backdate-days-select"
            value={maxBackdateDays}
            onChange={(e) => {
              const next = Number(e.target.value) as MaxBackdateDays;
              void familyService.updateMaxBackdateDays(next);
            }}
            style={{
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid var(--line-2)",
              fontSize: 14,
              background: "var(--surface)",
              color: "var(--ink)",
              fontFamily: "inherit",
              width: "fit-content",
            }}
          >
            {MAX_BACKDATE_DAYS_VALUES.map((v) => (
              <option key={v} value={v}>
                {maxBackdateDaysLabel(v)}
              </option>
            ))}
          </select>
        </Card>
      </div>
      {pendingChoice !== null && (
        <WeekStartDayConfirmModal
          current={current as WeekStartDay}
          next={pendingChoice}
          saving={saving}
          onCancel={() => setPendingChoice(null)}
          onConfirm={async () => {
            setSaving(true);
            try {
              await familyService.updateWeekStartDay(pendingChoice);
              setPendingChoice(null);
            } finally {
              setSaving(false);
            }
          }}
        />
      )}
    </div>
  );
}

function SegmentButton({
  label,
  active,
  onClick,
  testId,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      data-testid={testId}
      onClick={onClick}
      style={{
        padding: "10px 16px",
        background: active ? "var(--forest)" : "transparent",
        color: active ? "#fff" : "var(--ink)",
        border: "none",
        fontSize: 14,
        fontWeight: 600,
        cursor: active ? "default" : "pointer",
        fontFamily: "inherit",
      }}
    >
      {label}
    </button>
  );
}

function WeekStartDayConfirmModal({
  current,
  next,
  saving,
  onCancel,
  onConfirm,
}: {
  current: WeekStartDay;
  next: WeekStartDay;
  saving: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const today = new Date();
  const currentWeek = currentWeekBounds(current, today);
  const transition = transitionWeekAfter(currentWeek.end, next);
  const nextLabel = next === 1 ? "mandag – søndag" : "søndag – lørdag";
  return (
    <div
      data-testid="week-start-day-modal"
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        zIndex: 50,
      }}
      onClick={onCancel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--surface)",
          borderRadius: 14,
          maxWidth: 420,
          width: "100%",
          padding: 22,
          display: "flex",
          flexDirection: "column",
          gap: 12,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-display)",
            fontStyle: "italic",
            fontSize: 22,
            fontWeight: 400,
            letterSpacing: -0.4,
          }}
        >
          Endre ukestart?
        </div>
        <div style={{ fontSize: 14, color: "var(--ink-2)", lineHeight: 1.5 }}>
          Uka går framover til <strong>{nextLabel}</strong>. Den pågående uka
          fortsetter som før. Neste uke blir en overgangsuke på{" "}
          <strong>{transition.length_days} dager</strong>:
        </div>
        <div
          data-testid="week-start-day-modal-transition"
          style={{
            background: "var(--bg-2)",
            borderRadius: 10,
            padding: "10px 14px",
            fontSize: 14,
            fontWeight: 600,
          }}
        >
          {formatWeekRange(transition.start, transition.end)}
        </div>
        <div
          style={{
            display: "flex",
            gap: 8,
            marginTop: 6,
            justifyContent: "flex-end",
          }}
        >
          <Button
            variant="ghost"
            size="md"
            data-testid="week-start-day-modal-cancel"
            onClick={onCancel}
            disabled={saving}
          >
            Avbryt
          </Button>
          <Button
            variant="primary"
            size="md"
            data-testid="week-start-day-modal-confirm"
            onClick={onConfirm}
            disabled={saving}
          >
            {saving ? "Lagrer…" : "Bekreft"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function NotificationsSection() {
  const { profile, user } = useProfile();
  const install = useObservable(installPrompt.state$, installPrompt.snapshot);
  const push = useObservable(
    pushSubscriptionsService.state$,
    pushSubscriptionsService.snapshot,
  );
  const prefs = useObservable(
    notificationsService.state$,
    notificationsService.snapshot,
  );
  const [error, setError] = useState<string | null>(null);
  const [testStatus, setTestStatus] = useState<
    "idle" | "sending" | "sent" | "error"
  >("idle");

  useEffect(() => {
    if (
      push.permission === "granted" &&
      push.subscribed &&
      !prefs.loading &&
      !prefs.data &&
      profile &&
      user
    ) {
      void notificationsService.ensureDefaults(
        user.id,
        profile.data.family_id,
        profile.data.role,
      );
    }
  }, [push.permission, push.subscribed, prefs, profile, user]);

  if (!profile) return null;

  const iosBlocksPush = install.platform === "ios-safari" && !install.installed;

  return (
    <div data-testid="notifications-section">
      <SectionTitle>Varsler</SectionTitle>
      <Card>
        <PermissionState
          permission={push.permission}
          busy={push.busy}
          subscribed={push.subscribed}
          iosBlocked={iosBlocksPush}
          onEnable={async () => {
            if (!user) return;
            setError(null);
            try {
              await pushSubscriptionsService.enable(user.id);
            } catch (err) {
              setError(
                err instanceof Error
                  ? err.message
                  : "Kunne ikke aktivere varsler",
              );
            }
          }}
          onDisable={async () => {
            setError(null);
            try {
              await pushSubscriptionsService.disable();
            } catch (err) {
              setError(
                err instanceof Error ? err.message : "Kunne ikke deaktivere",
              );
            }
          }}
        />
        {error && (
          <div
            data-testid="notifications-error"
            style={{
              marginTop: 10,
              fontSize: 13,
              color: "var(--bad)",
            }}
          >
            {error}
          </div>
        )}
        {push.permission === "granted" && push.subscribed && prefs.data && (
          <>
            <div
              style={{
                margin: "16px -20px",
                borderTop: "1px solid var(--line)",
              }}
            />
            <PreferenceToggles
              role={profile.data.role}
              data={prefs.data}
              onUpdate={(patch) =>
                notificationsService.updatePreferences(patch)
              }
            />
            <div style={{ marginTop: 16 }}>
              <Button
                variant="ghost"
                size="sm"
                data-testid="test-notification-button"
                disabled={testStatus === "sending"}
                onClick={async () => {
                  setTestStatus("sending");
                  try {
                    await pushSubscriptionsService.sendTestNotification();
                    setTestStatus("sent");
                  } catch {
                    setTestStatus("error");
                  }
                }}
              >
                {testStatus === "sending"
                  ? "Sender…"
                  : testStatus === "sent"
                    ? "Sendt"
                    : testStatus === "error"
                      ? "Feilet — prøv igjen"
                      : "Send testvarsel"}
              </Button>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}

function PermissionState({
  permission,
  busy,
  subscribed,
  iosBlocked,
  onEnable,
  onDisable,
}: {
  permission: PermissionStatus;
  busy: boolean;
  subscribed: boolean;
  iosBlocked: boolean;
  onEnable: () => void;
  onDisable: () => void;
}) {
  if (permission === "unsupported") {
    return (
      <Message tone="muted">Denne nettleseren støtter ikke varsler.</Message>
    );
  }
  if (iosBlocked) {
    return (
      <Message tone="muted" testId="notifications-ios-install-first">
        Installer appen først (se ovenfor) for å aktivere varsler på iPhone og
        iPad.
      </Message>
    );
  }
  if (permission === "denied") {
    return (
      <Message tone="warn" testId="notifications-denied">
        Du har blokkert varsler i nettleseren. Skru på varsler for denne siden i
        nettleserinnstillingene, og last appen på nytt.
      </Message>
    );
  }
  const enabled = permission === "granted" && subscribed;
  return (
    <ToggleRow
      label="Send meg varsler"
      description="Du kan slå av enkelte typer under."
      checked={enabled}
      disabled={busy}
      onChange={(next) => (next ? onEnable() : onDisable())}
      testId="notifications-main-toggle"
    />
  );
}

function PreferenceToggles({
  role,
  data,
  onUpdate,
}: {
  role: "parent" | "child";
  data: NotificationPreferencesData;
  onUpdate: (patch: Partial<NotificationPreferencesData>) => void;
}) {
  if (role === "child") {
    const noLog = data.no_log_today ?? {
      enabled: false,
      time: "20:00",
      timezone: detectTimezone(),
    };
    const closeWeek: CloseWeekPreference = data.close_week ?? {
      enabled: false,
      time: "19:00",
      timezone: detectTimezone(),
      days_after_close: 0,
    };
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <TimedToggle
          label="Dagsmelding"
          description="Påminnelse hvis du ikke har logget noe i dag."
          value={noLog}
          onChange={(next) => onUpdate({ no_log_today: next })}
          testId="pref-no-log-today"
        />
        <CloseWeekToggle
          label="Avslutt uka"
          description="Påminnelse om å bekrefte uka."
          value={closeWeek}
          onChange={(next) => onUpdate({ close_week: next })}
          testId="pref-close-week"
        />
      </div>
    );
  }
  const kidConfirmed = data.kid_confirmed ?? { enabled: false };
  const approvalPending = data.approval_pending ?? { enabled: false };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <FlagToggle
        label="Barn har avsluttet uka"
        description="Få beskjed når et barn bekrefter uka."
        value={kidConfirmed}
        onChange={(next) => onUpdate({ kid_confirmed: next })}
        testId="pref-kid-confirmed"
      />
      <FlagToggle
        label="Jobb venter godkjenning"
        description="Få beskjed når et barn logger en jobb som krever godkjenning."
        value={approvalPending}
        onChange={(next) => onUpdate({ approval_pending: next })}
        testId="pref-approval-pending"
      />
    </div>
  );
}

function FlagToggle({
  label,
  description,
  value,
  onChange,
  testId,
}: {
  label: string;
  description: string;
  value: FlagPreference;
  onChange: (next: FlagPreference) => void;
  testId: string;
}) {
  return (
    <ToggleRow
      label={label}
      description={description}
      checked={value.enabled}
      onChange={(checked) => onChange({ enabled: checked })}
      testId={testId}
    />
  );
}

function TimedToggle({
  label,
  description,
  value,
  onChange,
  testId,
}: {
  label: string;
  description: string;
  value: TimedPreference;
  onChange: (next: TimedPreference) => void;
  testId: string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <ToggleRow
        label={label}
        description={description}
        checked={value.enabled}
        onChange={(checked) => onChange({ ...value, enabled: checked })}
        testId={testId}
      />
      {value.enabled && (
        <TimeInput
          value={value.time}
          onChange={(time) => onChange({ ...value, time })}
          testId={`${testId}-time`}
        />
      )}
    </div>
  );
}

function CloseWeekToggle({
  label,
  description,
  value,
  onChange,
  testId,
}: {
  label: string;
  description: string;
  value: CloseWeekPreference;
  onChange: (next: CloseWeekPreference) => void;
  testId: string;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <ToggleRow
        label={label}
        description={description}
        checked={value.enabled}
        onChange={(checked) => onChange({ ...value, enabled: checked })}
        testId={testId}
      />
      {value.enabled && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <DaysAfterCloseSelect
            value={value.days_after_close}
            onChange={(days_after_close) =>
              onChange({ ...value, days_after_close })
            }
            testId={`${testId}-days-after-close`}
          />
          <TimeInput
            value={value.time}
            onChange={(time) => onChange({ ...value, time })}
            testId={`${testId}-time`}
          />
        </div>
      )}
    </div>
  );
}

function ToggleRow({
  label,
  description,
  checked,
  disabled,
  onChange,
  testId,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  testId: string;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 12,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 14 }}>{label}</div>
        <div style={{ fontSize: 12, color: "var(--ink-3)", marginTop: 2 }}>
          {description}
        </div>
      </div>
      <ToggleSwitch
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        testId={testId}
      />
    </div>
  );
}

function ToggleSwitch({
  checked,
  disabled,
  onChange,
  testId,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        width: 44,
        height: 26,
        borderRadius: 999,
        background: checked ? "var(--forest)" : "var(--line-2)",
        border: "none",
        position: "relative",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        flexShrink: 0,
        transition: "background 0.15s",
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 3,
          left: checked ? 21 : 3,
          width: 20,
          height: 20,
          borderRadius: "50%",
          background: "#fff",
          transition: "left 0.15s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }}
      />
    </button>
  );
}

function TimeInput({
  value,
  onChange,
  testId,
}: {
  value: string;
  onChange: (next: string) => void;
  testId: string;
}) {
  return (
    <input
      type="time"
      value={value}
      step={900}
      onChange={(e) => onChange(snapTo15Minutes(e.target.value))}
      data-testid={testId}
      style={{
        padding: "8px 12px",
        borderRadius: 8,
        border: "1px solid var(--line-2)",
        fontSize: 14,
        background: "var(--surface)",
        color: "var(--ink)",
        fontFamily: "inherit",
        width: 120,
      }}
    />
  );
}

function DaysAfterCloseSelect({
  value,
  onChange,
  testId,
}: {
  value: DaysAfterClose;
  onChange: (next: DaysAfterClose) => void;
  testId: string;
}) {
  return (
    <select
      data-testid={testId}
      value={value}
      onChange={(e) => onChange(Number(e.target.value) as DaysAfterClose)}
      style={{
        padding: "8px 12px",
        borderRadius: 8,
        border: "1px solid var(--line-2)",
        fontSize: 14,
        background: "var(--surface)",
        color: "var(--ink)",
        fontFamily: "inherit",
        width: "fit-content",
      }}
    >
      {DAYS_AFTER_CLOSE_VALUES.map((d) => (
        <option key={d} value={d}>
          {daysAfterCloseLabel(d)}
        </option>
      ))}
    </select>
  );
}

function Message({
  children,
  tone,
  testId,
}: {
  children: React.ReactNode;
  tone: "muted" | "warn";
  testId?: string;
}) {
  const colors = {
    muted: { bg: "var(--bg-2)", fg: "var(--ink-2)", border: "var(--line)" },
    warn: { bg: "#F6E6C2", fg: "var(--warn)", border: "var(--warn)" },
  }[tone];
  return (
    <div
      data-testid={testId}
      style={{
        background: colors.bg,
        color: colors.fg,
        border: `1px solid ${colors.border}`,
        borderRadius: 10,
        padding: "12px 14px",
        fontSize: 13,
        lineHeight: 1.5,
      }}
    >
      {children}
    </div>
  );
}

function snapTo15Minutes(input: string): string {
  const [hh, mm] = input.split(":").map((n) => Number.parseInt(n, 10));
  if (Number.isNaN(hh) || Number.isNaN(mm)) return input;
  const snappedMinutes = Math.round(mm / 15) * 15;
  const finalMinutes = snappedMinutes === 60 ? 0 : snappedMinutes;
  const finalHour = snappedMinutes === 60 ? (hh + 1) % 24 : hh;
  return `${String(finalHour).padStart(2, "0")}:${String(finalMinutes).padStart(2, "0")}`;
}

function detectTimezone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || "Europe/Oslo";
  } catch {
    return "Europe/Oslo";
  }
}
